import React, { useRef } from 'react';
import { MediaItem, getMediaThumbnailUrl, isImageMediaItem } from '../../types/MediaItem';

export interface MediaThumbnailGridProps {
  /** Media items to display (images only, or filter by media_type) */
  items: MediaItem[];
  /** Selected media IDs */
  selectedIds: string[];
  /** Called when user clicks an item to toggle selection */
  onToggle: (mediaId: string) => void;
  /** Number of columns (default: 4) */
  columns?: 3 | 4 | 5 | 6 | 8 | 10 | 12;
  /** Thumbnail size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Max height for scroll container (e.g. 'max-h-64', 'max-h-80', 'max-h-96'). Ignored when fillAvailable is true. */
  maxHeight?: string;
  /** When true, grid fills the full available height of its flex parent (no max-height, uses flex-1) */
  fillAvailable?: boolean;
  /** Border/ring color when selected */
  selectedBorderColor?: string;
  /** Optional: filter to IMAGE only (default: show all) */
  imageOnly?: boolean;
  /** When true, use avatar-style borders (border-[#E5E5E5], selected: border-[#2f4941], hover: border-[#e4b372], rounded-2xl) */
  matchAvatarStyle?: boolean;
  /** When true, lay out thumbnails in a single horizontal row with overflow-x scroll (for contained image strip) */
  horizontalScroll?: boolean;
  /** When horizontalScroll: 'sm' = compact strip (25px), 'md' = gallery strip (w-40 h-40, min-h-[200px]) */
  horizontalStripSize?: 'sm' | 'md';
  /** When true, use teal selection style (border-teal-500, ring-teal-100, checkmark top-right) */
  tealSelection?: boolean;
  /** When horizontalScroll and false, root div has no overflow (parent is the scroll container) */
  scrollContainer?: boolean;
  /** When true, thumbnails get a short fade-in animation (e.g. for UGC gallery after upload) */
  animateEntry?: boolean;
  /** When true, tiles can be dragged onto the UGC timeline (sets dataTransfer). */
  enableLibraryDrag?: boolean;
  /** MIME type for library drag payload (must match timeline listener). */
  libraryDragMimeType?: string;
  /** Override the thumbnail cell aspect ratio (e.g. '9/16' for portrait). Defaults to square. */
  thumbnailAspectRatio?: string;
}

const sizeClasses = {
  sm: 'aspect-square w-full min-w-0',
  md: 'aspect-square w-full min-w-0',
  lg: 'aspect-square w-full min-w-0',
};

const gridCols: Record<number, string> = {
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  8: 'grid-cols-8',
  10: 'grid-cols-10',
  12: 'grid-cols-12',
};

export const MediaThumbnailGrid: React.FC<MediaThumbnailGridProps> = ({
  items,
  selectedIds,
  onToggle,
  columns = 4,
  size = 'md',
  maxHeight = 'max-h-80',
  fillAvailable = false,
  selectedBorderColor = 'border-[#A3B18A] ring-2 ring-[#A3B18A]/30',
  imageOnly = false,
  matchAvatarStyle = false,
  horizontalScroll = false,
  horizontalStripSize = 'sm',
  tealSelection = false,
  scrollContainer = true,
  animateEntry = false,
  enableLibraryDrag = false,
  libraryDragMimeType = 'application/x-ugc-media-id',
  thumbnailAspectRatio,
}) => {
  const filtered = imageOnly ? items.filter((i) => isImageMediaItem(i)) : items;
  const skipClickAfterDrag = useRef(false);

  const gapClass = columns >= 6 ? 'gap-2' : 'gap-3';
  const gridContainerClasses = fillAvailable
    ? `grid auto-rows-min ${gridCols[columns] ?? 'grid-cols-4'} ${gapClass} items-start justify-items-stretch overflow-y-auto flex-1 min-h-0 pr-1`
    : `grid auto-rows-min ${gridCols[columns] ?? 'grid-cols-4'} ${gapClass} items-start justify-items-stretch overflow-y-auto ${maxHeight} pr-1`;

  const isHorizontalMd = horizontalScroll && horizontalStripSize === 'md';
  const overflowClass = scrollContainer ? 'overflow-x-auto overflow-y-hidden' : '';
  const horizontalContainerClasses = isHorizontalMd
    ? `flex flex-nowrap gap-2 ${overflowClass} pr-1 h-full w-full min-w-0 items-center`
    : `flex flex-nowrap gap-2 ${overflowClass} overflow-y-hidden pr-1 h-[25px] w-full min-w-0 items-center`;

  const baseBtn = 'relative block w-full overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#333333]/20';
  const buttonClasses = tealSelection
    ? (isSelected: boolean) =>
        `${baseBtn} rounded-lg bg-transparent ${isSelected ? 'border-teal-500 ring-2 ring-teal-100' : 'border-transparent hover:border-teal-500'}`
    : matchAvatarStyle
    ? (isSelected: boolean) =>
        `${baseBtn} rounded-2xl ${isSelected ? 'border-[#2f4941] ring-2 ring-[#2f4941] ring-offset-2 ring-offset-white' : 'border-[#E5E5E5] hover:border-[#e4b372]'}`
    : (isSelected: boolean) =>
        `${baseBtn} rounded-xl ${isSelected ? selectedBorderColor : 'border-transparent hover:border-[#E5E5E5] hover:shadow-sm'}`;

  const checkmarkBg = tealSelection ? 'bg-teal-500' : matchAvatarStyle ? 'bg-[#2f4941]' : 'bg-[#A3B18A]';
  const checkmarkPos = tealSelection ? 'top-2 right-2' : 'top-1 left-1';
  const thumbSize = horizontalScroll
    ? isHorizontalMd
      ? 'h-[120px] w-auto flex-shrink-0'
      : 'w-[25px] h-[25px] flex-shrink-0'
    : 'w-full';
  const thumbInner = horizontalScroll
    ? isHorizontalMd
      ? 'h-full overflow-hidden'
      : 'w-full h-full aspect-square overflow-hidden'
    : sizeClasses[size];
  const imgClasses = horizontalScroll && isHorizontalMd ? 'h-full w-auto object-contain' : 'w-full h-full object-cover';

  if (horizontalScroll) {
    return (
      <div
        className={horizontalContainerClasses}
        style={{ scrollbarWidth: 'thin' }}
      >
        {filtered.map((item) => {
          const isSelected = selectedIds.includes(item.media_id);
          const thumbSrc = getMediaThumbnailUrl(item);
          return (
            <button
              key={item.media_id}
              type="button"
              onClick={() => onToggle(item.media_id)}
              className={`${buttonClasses(isSelected)} ${thumbSize} ${animateEntry ? 'thumbnail-enter' : ''}`}
            >
              <div className={thumbInner}>
                {thumbSrc ? (
                  <img
                    src={thumbSrc}
                    alt={item.s3_key?.split('/').pop() || 'Media'}
                    className={imgClasses}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[#F1F5F9] dark:bg-surface-ui-hover text-[10px] font-medium text-[#94A3B8] dark:text-theme-fg-muted">
                    No URL
                  </div>
                )}
              </div>
              {isSelected && (
                <div className={`absolute ${checkmarkPos} w-4 h-4 ${checkmarkBg} rounded-full flex items-center justify-center border-2 border-white p-1`}>
                  <svg className="text-white w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={gridContainerClasses}
      style={{ scrollbarWidth: 'thin' }}
    >
        {filtered.map((item) => {
          const isSelected = selectedIds.includes(item.media_id);
          const thumbSrc = getMediaThumbnailUrl(item);
          const isVideo = (item.media_type || '').trim().toLowerCase() === 'video';
          return (
            <button
              key={item.media_id}
              type="button"
              draggable={enableLibraryDrag}
              onDragStart={(e) => {
                if (!enableLibraryDrag) return;
                e.dataTransfer.setData(libraryDragMimeType, item.media_id);
                e.dataTransfer.setData('text/plain', `__UGC_MEDIA__:${item.media_id}`);
                e.dataTransfer.effectAllowed = 'copy';
                skipClickAfterDrag.current = true;
              }}
              onDragEnd={() => {
                window.setTimeout(() => {
                  skipClickAfterDrag.current = false;
                }, 0);
              }}
              onClick={() => {
                if (skipClickAfterDrag.current) return;
                onToggle(item.media_id);
              }}
              className={`${buttonClasses(isSelected)} ${animateEntry ? 'thumbnail-enter' : ''} ${enableLibraryDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
              <div
                className={`relative ${thumbnailAspectRatio ? 'w-full min-w-0' : 'aspect-square w-full min-w-0'}`}
                style={thumbnailAspectRatio ? { aspectRatio: thumbnailAspectRatio } : undefined}
              >
                {thumbSrc ? (
                  isVideo ? (
                    <video
                      src={thumbSrc}
                      className="h-full w-full object-cover bg-black"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={thumbSrc}
                      alt={item.s3_key?.split('/').pop() || 'Media'}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  )
                ) : (
                  <div className="flex h-full min-h-[4.5rem] w-full items-center justify-center bg-[#F1F5F9] dark:bg-surface-ui-hover text-[10px] font-medium text-[#94A3B8] dark:text-theme-fg-muted">
                    No URL
                  </div>
                )}
                {isVideo && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <svg className="h-7 w-7 drop-shadow-lg" viewBox="0 0 24 24" fill="white" aria-hidden>
                      <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.45)" />
                      <polygon points="10,8 17,12 10,16" fill="white" />
                    </svg>
                  </div>
                )}
              </div>
            {isSelected && (
              <div className={`absolute top-1 left-1 w-4 h-4 ${checkmarkBg} rounded-full flex items-center justify-center border-2 border-white`}>
                <svg
                  className={`text-white ${matchAvatarStyle ? 'w-2.5 h-2.5' : 'w-4 h-4'}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default MediaThumbnailGrid;
